/**
 * WorkplanView — CRUD-версія (рефактор з JSONB blob на нормалізовані таблиці).
 *
 * Бекенд: /api/projects/:id/workplan
 *   GET / — повний план {months: [{id, month, title, ..., tasks:[]}]}
 *   POST /seed — клонувати template (для свіжих проектів)
 *   POST /migrate-from-jsonb — імпорт зі старого work_plans.plan
 *   POST/PUT/DELETE /months, /months/:id, /months/:id/items, /items/:id, /items/:id/toggle
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useApp } from "../context/AppContext";

const STATUS_OPT = [
  { value: "pending",  label: "В черзі",        color: "#6b7280" },
  { value: "active",   label: "В роботі",       color: "#6366f1" },
  { value: "done",     label: "Виконано",       color: "#10b981" },
  { value: "skipped",  label: "Пропущено",      color: "#94a3b8" },
];
const statusMeta = (s) => STATUS_OPT.find(x => x.value === s) || STATUS_OPT[0];

const ICONS_POOL = ["🚀", "📊", "🔧", "🎯", "📈", "💎", "🏆", "📝", "🔍", "⚙️", "🌟", "📌", "🎨", "🔑", "📦"];

export default function WorkplanView() {
  const { proj, activeId, apiFetch, flash, dark, t, card, btnP, btnG, btnSm } = useApp();
  if (!proj) return null;

  const [plan, setPlan] = useState({ months: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [drag, setDrag] = useState(null);       // { monthId, index, itemId } — задача, яку тягнемо
  const [dragOver, setDragOver] = useState(null); // id задачі, над якою зараз курсор

  // Editing state — {kind: "month"|"item", id, field, value}
  const [editing, setEditing] = useState(null);
  // Adding state — {kind, parentId?, draft}
  const [adding, setAdding] = useState(null);
  // View mode: "manager" or "client"
  const [viewMode, setViewMode] = useState("manager");

  // ─── Load ───
  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/workplan`);
      const data = await r.json();
      setPlan(data && Array.isArray(data.months) ? data : { months: [] });
    } catch (e) {
      flash("Помилка завантаження: " + e.message);
    }
    setLoading(false);
  };
  useEffect(() => { if (activeId) load(); }, [activeId]);

  // ─── Actions ───
  const callApi = async (path, method = "POST", body) => {
    const r = await apiFetch(`/api/projects/${activeId}/workplan${path}`, {
      method,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await r.json().catch(() => ({}));
    if (data.error) throw new Error(data.error);
    return data;
  };

  const seedTemplate = async () => {
    if (!confirm("Завантажити стандартний 7-місячний план зі шаблону?")) return;
    try {
      const r = await callApi("/seed", "POST");
      flash(`Створено ${r.monthsCreated} місяців · ${r.itemsCreated} задач`);
      await load();
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const migrateLegacy = async () => {
    if (!confirm("Імпортувати план зі старої таблиці (work_plans.plan)?")) return;
    try {
      const r = await callApi("/migrate-from-jsonb", "POST");
      flash(`Імпортовано ${r.monthsCreated} місяців · ${r.itemsCreated} задач`);
      await load();
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const addMonth = async () => {
    try {
      const created = await callApi("/months", "POST", {
        title: `Новий місяць`,
        block: "Custom",
        icon: ICONS_POOL[Math.floor(Math.random() * ICONS_POOL.length)],
        color: "#a855f7",
      });
      if (created && created.id) {
        const month = { ...created, month: created.month_no, tasks: [] };
        setPlan(prev => ({ ...prev, months: [...(prev.months || []), month] }));
      } else { await load(); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  // Усі мутації — ОПТИМІСТИЧНІ (міняємо локальний стан, без повного load(), щоб не «перезавантажувати»
  // список і не стрибати скролом). load() лише як відкат при помилці.
  const updateMonth = async (monthId, fields) => {
    setPlan(prev => ({ ...prev, months: (prev.months || []).map(m => m.id === monthId ? { ...m, ...fields, month: fields.month_no != null ? fields.month_no : m.month } : m) }));
    try { await callApi(`/months/${monthId}`, "PUT", fields); }
    catch (e) { flash("Помилка: " + e.message); await load(); }
  };

  const deleteMonth = async (monthId, taskCount) => {
    const msg = taskCount > 0
      ? `Видалити місяць і всі ${taskCount} задач у ньому?`
      : "Видалити місяць?";
    if (!confirm(msg)) return;
    setPlan(prev => ({ ...prev, months: (prev.months || []).filter(m => m.id !== monthId) }));
    try { await callApi(`/months/${monthId}`, "DELETE"); }
    catch (e) { flash("Помилка: " + e.message); await load(); }
  };

  const addItem = async (monthId) => {
    try {
      const created = await callApi(`/months/${monthId}/items`, "POST", {
        title: "Нова задача", description_manager: "", description_client: "", duration: "", status: "pending",
      });
      if (created && created.id) {
        setPlan(prev => ({ ...prev, months: (prev.months || []).map(m => m.id === monthId ? { ...m, tasks: [...(m.tasks || []), created] } : m) }));
      } else { await load(); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const updateItem = async (itemId, fields) => {
    // Синхронізуємо аліаси (API віддає manager/description = description_manager, client/clientValue = description_client),
    // інакше UI читає старий tk.manager і опис «відкочується» після редагування/видалення.
    const patch = { ...fields };
    if ("description_manager" in fields) { patch.manager = fields.description_manager; patch.description = fields.description_manager; }
    if ("description_client" in fields) { patch.client = fields.description_client; patch.clientValue = fields.description_client; }
    setPlan(prev => ({ ...prev, months: (prev.months || []).map(m => ({ ...m, tasks: (m.tasks || []).map(tk => tk.id === itemId ? { ...tk, ...patch } : tk) })) }));
    try { await callApi(`/items/${itemId}`, "PUT", fields); }
    catch (e) { flash("Помилка: " + e.message); await load(); }
  };

  const deleteItem = async (itemId, title) => {
    if (!confirm(`Видалити задачу "${title}"?`)) return;
    setPlan(prev => ({ ...prev, months: (prev.months || []).map(m => ({ ...m, tasks: (m.tasks || []).filter(tk => tk.id !== itemId) })) }));
    try { await callApi(`/items/${itemId}`, "DELETE"); }
    catch (e) { flash("Помилка: " + e.message); await load(); }
  };

  const toggleItem = async (itemId) => {
    // Оптимістично міняємо статус ЛОКАЛЬНО (без повного load() — інакше список перемальовується
    // й скрол стрибає вгору, що сприймається як «перезавантаження сторінки»).
    setPlan(prev => ({
      ...prev,
      months: (prev.months || []).map(m => ({
        ...m,
        tasks: (m.tasks || []).map(tk => tk.id === itemId
          ? { ...tk, status: tk.status === "done" ? "pending" : "done" }
          : tk),
      })),
    }));
    try {
      await callApi(`/items/${itemId}/toggle`, "POST");
    } catch (e) {
      flash("Помилка: " + e.message);
      await load(); // відкат до серверного стану лише при помилці
    }
  };

  // Перетягування задач у межах місяця: оптимістично міняємо порядок локально + зберігаємо position.
  const reorderItems = async (monthId, fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    let newTasks = null;
    setPlan(prev => ({
      ...prev,
      months: (prev.months || []).map(m => {
        if (m.id !== monthId) return m;
        const tasks = [...(m.tasks || [])];
        if (fromIdx < 0 || fromIdx >= tasks.length) return m;
        const [moved] = tasks.splice(fromIdx, 1);
        tasks.splice(toIdx, 0, moved);
        newTasks = tasks;
        return { ...m, tasks };
      }),
    }));
    if (newTasks) {
      const items = newTasks.map((tk, i) => ({ id: tk.id, position: i }));
      try { await callApi("/reorder", "POST", { items }); }
      catch (e) { flash("Помилка: " + e.message); await load(); }
    }
  };

  // InlineEdit винесено на рівень модуля (нижче), щоб не перемонтовуватись при кожному ре-рендері.

  // ─── Render ───
  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Завантаження…</div>;

  const empty = plan.months.length === 0;
  const totalTasks = plan.months.reduce((s, m) => s + (m.tasks?.length || 0), 0);
  const totalDone = plan.months.reduce((s, m) => s + (m.tasks?.filter(tk => tk.status === "done").length || 0), 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>📋 План робіт</h2>
        {!empty && (
          <span style={{ fontSize: 12, color: t.textMuted }}>
            {plan.months.length} місяців · {totalDone}/{totalTasks} задач
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={() => setViewMode(viewMode === "manager" ? "client" : "manager")}
            title="Перемкнути вигляд (менеджер ↔ клієнт)" style={{ ...btnSm, fontSize: 11 }}>
            👁 {viewMode === "manager" ? "Менеджер" : "Клієнт"}
          </button>
          {!empty && (
            <button onClick={addMonth} style={{ ...btnP, fontSize: 12 }}>+ Місяць</button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {empty && (
        <div style={{ ...card, padding: 40, textAlign: "center" }}>
          <p style={{ fontSize: 14, color: t.textMuted, marginBottom: 20 }}>
            План робіт ще не створено для цього проекту.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={seedTemplate} style={{ ...btnP, fontSize: 13, padding: "8px 16px" }}>
              🚀 Завантажити стандартний 7-місячний план
            </button>
            <button onClick={migrateLegacy} style={{ ...btnG, fontSize: 13, padding: "8px 16px" }}>
              ⬆ Імпортувати зі старого (work_plans)
            </button>
            <button onClick={addMonth} style={{ ...btnG, fontSize: 13, padding: "8px 16px" }}>
              + Створити порожній місяць
            </button>
          </div>
        </div>
      )}

      {/* Months */}
      {!empty && plan.months.map((m) => {
        const tasks = m.tasks || [];
        const done = tasks.filter(tk => tk.status === "done").length;
        const total = tasks.length;
        const pct = total > 0 ? Math.round(done / total * 100) : 0;
        const isOpen = expanded.has(m.id);

        return (
          <div key={m.id} style={{ ...card, marginBottom: 12, overflow: "hidden" }}>
            {/* Month header */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
              background: isOpen ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)") : "transparent",
              borderBottom: isOpen ? `1px solid ${t.border}` : "none",
              cursor: "pointer",
            }}
              onClick={() => {
                const next = new Set(expanded);
                if (next.has(m.id)) next.delete(m.id);
                else next.add(m.id);
                setExpanded(next);
              }}>
              <span style={{ fontSize: 22 }}>{m.icon || "📅"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={e => e.stopPropagation()}>
                  <span style={{ fontSize: 11, color: m.color || "#6366f1", fontWeight: 700, letterSpacing: ".1em" }}>
                    М<InlineEdit value={m.month} placeholder="?" width={22}
                      onSave={(v) => { const n = parseInt(String(v).replace(/\D/g, ""), 10); if (Number.isFinite(n)) updateMonth(m.id, { month_no: n }); }} />
                  </span>
                  <strong style={{ fontSize: 15 }}>
                    <InlineEdit value={m.title} onSave={(v) => updateMonth(m.id, { title: v })} />
                  </strong>
                </div>
                <div onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                  <InlineEdit value={m.block} placeholder="Блок (необов'язково)" onSave={(v) => updateMonth(m.id, { block: v })} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>{done}/{total}</span>
                <div style={{ width: 80, height: 6, background: t.border, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: m.color || "#6366f1", transition: "width .3s" }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: m.color || "#6366f1", minWidth: 36, textAlign: "right" }}>{pct}%</span>
                <button onClick={(e) => { e.stopPropagation(); deleteMonth(m.id, total); }}
                  title="Видалити місяць" style={{ ...btnSm, fontSize: 11, color: "#ef4444", border: 0, background: "transparent" }}>
                  🗑
                </button>
                <span style={{ fontSize: 14, color: t.textMuted, transition: "transform .2s", transform: isOpen ? "rotate(90deg)" : "" }}>›</span>
              </div>
            </div>

            {/* Tasks */}
            {isOpen && (
              <div>
                {tasks.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: t.textMuted, fontSize: 12, fontStyle: "italic" }}>
                    Задач ще немає
                  </div>
                )}
                {tasks.map((tk, ti) => {
                  const stm = statusMeta(tk.status);
                  const isDragOver = drag && drag.monthId === m.id && dragOver === tk.id && drag.itemId !== tk.id;
                  return (
                    <div key={tk.id}
                      onDragOver={(e) => { if (drag && drag.monthId === m.id) { e.preventDefault(); setDragOver(tk.id); } }}
                      onDragLeave={() => setDragOver(o => o === tk.id ? null : o)}
                      onDrop={() => { if (drag && drag.monthId === m.id) reorderItems(m.id, drag.index, ti); setDrag(null); setDragOver(null); }}
                      style={{
                      display: "flex", gap: 10, padding: "10px 16px", alignItems: "flex-start",
                      borderBottom: `1px solid ${t.border}`,
                      borderTop: isDragOver ? `2px solid ${stm.color}` : "2px solid transparent",
                      background: tk.status === "done" ? (dark ? "rgba(16,185,129,0.04)" : "rgba(16,185,129,0.03)") : "transparent",
                    }}>
                      {/* drag handle */}
                      <span draggable
                        onDragStart={() => { setDrag({ monthId: m.id, index: ti, itemId: tk.id }); }}
                        onDragEnd={() => { setDrag(null); setDragOver(null); }}
                        title="Перетягни, щоб змінити порядок"
                        style={{ cursor: "grab", color: t.textMuted, fontSize: 14, lineHeight: 1, marginTop: 3, userSelect: "none", padding: "0 2px" }}>⠿</span>
                      {/* checkbox */}
                      <input type="checkbox"
                        checked={tk.status === "done"}
                        onChange={() => toggleItem(tk.id)}
                        style={{ marginTop: 4, cursor: "pointer", accentColor: stm.color }}
                      />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* title row */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontFamily: "monospace", color: t.textMuted, minWidth: 28 }}>
                            <InlineEdit value={tk.stage_no} placeholder="—" onSave={(v) => updateItem(tk.id, { stage_no: v })} />
                          </span>
                          <strong style={{
                            fontSize: 13, flex: 1,
                            textDecoration: tk.status === "done" ? "line-through" : "none",
                            color: tk.status === "done" ? t.textMuted : t.text,
                          }}>
                            <InlineEdit value={tk.title} onSave={(v) => updateItem(tk.id, { title: v })} />
                          </strong>

                          <select value={tk.status}
                            onChange={(e) => updateItem(tk.id, { status: e.target.value })}
                            style={{
                              fontSize: 10, padding: "2px 6px", borderRadius: 3,
                              border: `1px solid ${stm.color}`, color: stm.color, background: "transparent",
                              fontWeight: 700, cursor: "pointer",
                            }}>
                            {STATUS_OPT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>

                          <span style={{ fontSize: 11, color: t.textMuted, minWidth: 50, textAlign: "right" }}>
                            <InlineEdit value={tk.duration} placeholder="час" onSave={(v) => updateItem(tk.id, { duration: v })} />
                          </span>

                          <button onClick={() => deleteItem(tk.id, tk.title)} title="Видалити"
                            style={{ ...btnSm, fontSize: 11, color: "#ef4444", border: 0, background: "transparent", padding: "2px 4px" }}>
                            ✕
                          </button>
                        </div>

                        {/* description */}
                        <div style={{ fontSize: 11, color: t.textMuted, paddingLeft: 36 }}>
                          {viewMode === "manager" ? (
                            <InlineEdit value={tk.manager || tk.description_manager}
                              placeholder="Опис для менеджера (клацни щоб додати)"
                              multiline={true} linkify={true}
                              onSave={(v) => updateItem(tk.id, { description_manager: v })} />
                          ) : (
                            <InlineEdit value={tk.client || tk.description_client}
                              placeholder="Опис для клієнта (клацни щоб додати)"
                              multiline={true} linkify={true}
                              onSave={(v) => updateItem(tk.id, { description_client: v })} />
                          )}
                          <TaskAttachments items={tk.attachments || []} onChange={(arr) => updateItem(tk.id, { attachments: arr })} />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add task */}
                <div style={{ padding: "10px 16px", display: "flex", justifyContent: "flex-start", borderTop: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }}>
                  <button onClick={() => addItem(m.id)} style={{ ...btnSm, fontSize: 11 }}>
                    + Додати задачу
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Інлайн-поле редагування. ВАЖЛИВО: на рівні модуля (стабільна ідентичність) —
// інакше при кожному ре-рендері WorkplanView React перемонтовував би інпут і скидав
// редагування (баг «через 2с не можна редагувати» під час фонових задач).
// Перетворює URL у тексті на клікабельні посилання (для описів у плані робіт).
function linkifyText(text) {
  return String(text).split(/(https?:\/\/[^\s<>()]+)/g).map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
          style={{ color: "#60a5fa", textDecoration: "underline", wordBreak: "break-all" }}>{p}</a>
      : p
  );
}

function TaskAttachments({ items, onChange }) {
  const { t } = useApp();
  const inpRef = useRef(null);
  const MAX = 3 * 1024 * 1024;
  const add = (e) => {
    const files = Array.from(e.target.files || []);
    let pending = files.length; if (!pending) return;
    const next = [...(items || [])];
    files.forEach(f => {
      if (f.size > MAX) { alert(`Файл "${f.name}" більший за 3 МБ`); pending--; if (!pending) onChange(next); return; }
      const r = new FileReader();
      r.onload = () => { next.push({ name: f.name, type: f.type, size: f.size, data: r.result }); pending--; if (!pending) onChange(next); };
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };
  const remove = (i) => { const n = [...(items || [])]; n.splice(i, 1); onChange(n); };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5, alignItems: "center" }}>
      {(items || []).map((f, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(99,102,241,0.10)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 6, padding: "2px 7px", fontSize: 10.5 }}>
          <a href={f.data} download={f.name} title={f.name} style={{ color: "#6366f1", textDecoration: "none", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>\ud83d\udcce {f.name}</a>
          <button onClick={() => remove(i)} title="Прибрати" style={{ border: 0, background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1 }}>\u00d7</button>
        </span>
      ))}
      <button onClick={() => inpRef.current && inpRef.current.click()} title="Прикріпити файл (до 3 МБ)" style={{ border: `1px dashed ${t.textMuted}`, background: "transparent", color: t.textMuted, borderRadius: 6, padding: "2px 9px", fontSize: 10.5, cursor: "pointer" }}>\ud83d\udcce Додати файл</button>
      <input ref={inpRef} type="file" multiple onChange={add} style={{ display: "none" }} />
    </div>
  );
}

function InlineEdit({ value, onSave, multiline = false, placeholder, width = "auto", linkify = false }) {
  const { dark, t } = useApp();
  const [v, setV] = useState(value || "");
  const [editingNow, setEditingNow] = useState(false);
  useEffect(() => { setV(value || ""); }, [value]);

  if (!editingNow) {
    return (
      <span onClick={() => setEditingNow(true)} title="Клацни щоб редагувати" style={{
        cursor: "text", padding: "1px 4px", borderRadius: 3,
        background: "transparent", transition: "background .15s",
        minWidth: 20, display: "inline-block", whiteSpace: multiline ? "pre-wrap" : "normal",
      }}
        onMouseEnter={e => e.currentTarget.style.background = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >{value ? (linkify ? linkifyText(value) : value) : <span style={{ color: t.textMuted, fontStyle: "italic" }}>{placeholder || "—"}</span>}</span>
    );
  }

  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag
      autoFocus
      value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditingNow(false); if (v !== value) onSave(v); }}
      onKeyDown={e => {
        if (e.key === "Escape") { setV(value || ""); setEditingNow(false); }
        if (e.key === "Enter" && !multiline) { setEditingNow(false); if (v !== value) onSave(v); }
      }}
      rows={multiline ? 3 : undefined}
      style={{
        width: width === "auto" ? "100%" : width,
        padding: "3px 6px",
        fontSize: "inherit", fontFamily: "inherit", color: t.text,
        background: t.bg, border: `1px solid ${t.border}`, borderRadius: 4, outline: "none",
        resize: multiline ? "vertical" : "none",
      }}
    />
  );
}
